import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../../components/Header';
import dependentMaterialService from '../../services/dependentMaterialService';
import './DependentMaterialList.scss';

const DependentMaterialList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  
  const [dependentMaterials, setDependentMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  useEffect(() => {
    fetchDependentMaterials();
  }, []);

  const fetchDependentMaterials = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Try authenticated endpoint first, fallback to public if needed
      let result = await dependentMaterialService.getAllDependentMaterialsAuth();
      
      if (!result.success) {
        // Fallback to public endpoint
        result = await dependentMaterialService.getAllDependentMaterials();
      }
      
      if (result.success) {
        setDependentMaterials(result.data || []);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to fetch dependent materials');
      console.error('Error fetching dependent materials:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMaterialSelect = (material) => {
    setSelectedMaterial(material);
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  const getDisplayName = (name) => {
    if (typeof name === 'object' && name !== null) {
      return name[i18n.language] || name.az || name.en || name.ru || name.text || Object.values(name)[0];
    }
    return name || t('dependentMaterialList.unknownMaterial');
  };

  const getDisplayDescription = (description) => {
    if (typeof description === 'object' && description !== null) {
      return description[i18n.language] || description.az || description.en || description.ru || description.text || Object.values(description)[0];
    }
    return description || t('dependentMaterialList.noDescription');
  };

  const getCategoryNames = (categories) => {
    if (!categories || !Array.isArray(categories) || categories.length === 0) {
      return t('dependentMaterialList.noCategory');
    }
    
    return categories.map(category => {
      if (typeof category === 'object' && category.name) {
        return getDisplayName(category.name);
      }
      return category;
    }).join(', ');
  };

  // Filter materials based on search term, category, status and default status
  const filteredMaterials = dependentMaterials.filter(material => {
    // Only show active materials that are default
    const isActiveAndDefault = (material.status === true || material.status === undefined) && 
                              (material.isActive === true || material.isActive === undefined) &&
                              material.isDefault === true;
    
    if (!isActiveAndDefault) return false;
    
    const nameMatch = getDisplayName(material.name).toLowerCase().includes(searchTerm.toLowerCase());
    const descriptionMatch = getDisplayDescription(material.description).toLowerCase().includes(searchTerm.toLowerCase());
    
    const categoryMatch = !selectedCategory || 
      (material.categories && material.categories.some(cat => 
        getDisplayName(cat.name).toLowerCase().includes(selectedCategory.toLowerCase())
      ));
    
    return (nameMatch || descriptionMatch) && categoryMatch;
  });

  // Get unique categories for filter
  const allCategories = dependentMaterials.reduce((categories, material) => {
    if (material.categories && Array.isArray(material.categories)) {
      material.categories.forEach(category => {
        const categoryName = getDisplayName(category.name);
        if (categoryName && !categories.includes(categoryName)) {
          categories.push(categoryName);
        }
      });
    }
    return categories;
  }, []);

  const renderMaterialCard = (material) => (
    <div 
      key={material._id || material.id} 
      className={`material-card ${selectedMaterial?._id === material._id || selectedMaterial?.id === material.id ? 'selected' : ''}`}
      onClick={() => handleMaterialSelect(material)}
    >
      <div className="material-header">
        <div className="material-name">
          {getDisplayName(material.name)}
        </div>
        {material.isDefault && (
          <span className="default-badge">{t('dependentMaterialList.default')}</span>
        )}
      </div>
      
      <div className="material-info">
        <div className="material-description">
          {getDisplayDescription(material.description)}
        </div>
        
        <div className="material-details">
          <div className="detail-item">
            <span className="label">{t('dependentMaterialList.category')}:</span>
            <span className="value">{getCategoryNames(material.categories)}</span>
          </div>
          
          <div className="detail-item">
            <span className="label">{t('dependentMaterialList.unitOfMeasure')}:</span>
            <span className="value">{material.unitOfMeasure || 'N/A'}</span>
          </div>
          
          <div className="detail-item" style={{display: 'none', opacity: 0}}>
            <span className="label">{t('dependentMaterialList.usagePerSqm')}:</span>
            <span className="value">{material.usagePerSquareMeter || 0}</span>
          </div>
        </div>
      </div>
      
      <div className="material-pricing" style={{display: 'none', opacity: 0}}>
        <div className="price-item">
          <span className="price-label">{t('dependentMaterialList.price')}:</span>
          <span className="price-value">
            {material.price || 0} {material.currency || 'AZN'}
          </span>
        </div>
        
        {material.servicePrice && (
          <div className="price-item">
            <span className="price-label">{t('dependentMaterialList.servicePrice')}:</span>
            <span className="price-value">
              {material.servicePrice} {material.currency || 'AZN'}
            </span>
          </div>
        )}
      </div>
      
      <div className="material-status">
        <span className={`status-badge ${(material.status === true || material.status === undefined) && (material.isActive === true || material.isActive === undefined) ? 'active' : 'inactive'}`}>
          {(material.status === true || material.status === undefined) && (material.isActive === true || material.isActive === undefined)
            ? t('dependentMaterialList.active') 
            : t('dependentMaterialList.inactive')
          }
        </span>
      </div>
    </div>
  );

  return (
    <div className="dependent-material-list-page">
      <Header
        title={t('dependentMaterialList.title')}
        showProfileIcon={true}
        showBackIcon={true}
        onBackClick={handleBackClick}
      />

      <div className="container dependent-material-list-content">
        {loading ? (
          <div className="loading-container">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </div>
            <p className="mt-2">{t('dependentMaterialList.loadingMaterials')}</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <div className="alert alert-danger">
              <h5>{t('common.error')}</h5>
              <p>{error}</p>
              <button 
                className="btn btn-outline-danger"
                onClick={fetchDependentMaterials}
                disabled={loading}
              >
                <i className="bi bi-arrow-clockwise me-1"></i>
                {loading ? t('common.loading') : t('dependentMaterialList.retry')}
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Summary and Filters */}
            <div className="material-summary">
              <div className="summary-header">
              <h6>{t('dependentMaterialList.summary')}</h6>
                <button 
                  className="btn btn-outline-primary btn-sm"
                  onClick={fetchDependentMaterials}
                  disabled={loading}
                >
                  <i className="bi bi-arrow-clockwise me-1"></i>
                  {loading ? t('common.loading') : t('dependentMaterialList.refresh')}
                </button>
              </div>
              <div className="summary-stats">
                <div className="stat-item">
                  <span className="stat-label">{t('dependentMaterialList.totalMaterials')}:</span>
                  <span className="stat-value">{dependentMaterials.length}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">{t('dependentMaterialList.activeMaterials')}:</span>
                  <span className="stat-value">
                    {dependentMaterials.filter(m => (m.status === true || m.status === undefined) && (m.isActive === true || m.isActive === undefined) && m.isDefault === true).length}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">{t('dependentMaterialList.filteredResults')}:</span>
                  <span className="stat-value">{filteredMaterials.length}</span>
                </div>
              </div>
            </div>

            {/* Search and Filter Controls */}
            <div className="filter-controls">
              <div className="search-input">
                <input
                  type="text"
                  className="form-control"
                  placeholder={t('dependentMaterialList.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <i className="bi bi-search search-icon"></i>
              </div>
              
              <div className="category-filter">
                <select
                  className="form-select"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="">{t('dependentMaterialList.allCategories')}</option>
                  {allCategories.map((category, index) => (
                    <option key={index} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              {(searchTerm || selectedCategory) && (
                <button
                  className="btn btn-outline-secondary clear-filters"
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedCategory('');
                  }}
                >
                  <i className="bi bi-x-circle me-1"></i>
                  {t('dependentMaterialList.clearFilters')}
                </button>
              )}
            </div>

            {/* Material List */}
            <div className="material-list">
              {filteredMaterials.length === 0 ? (
                <div className="no-materials">
                  <div className="alert alert-info">
                    <i className="bi bi-info-circle me-2"></i>
                    {searchTerm || selectedCategory 
                      ? t('dependentMaterialList.noFilteredMaterials')
                      : t('dependentMaterialList.noMaterials')
                    }
                  </div>
                </div>
              ) : (
                <div className="materials-grid">
                  {filteredMaterials.map(renderMaterialCard)}
                </div>
              )}
            </div>

            {/* Selected Material Details */}
            {selectedMaterial && (
              <div className="selected-material-details">
                <h6>{t('dependentMaterialList.materialDetails')}</h6>
                <div className="details-card">
                  <div className="detail-row">
                    <span className="label">{t('dependentMaterialList.name')}:</span>
                    <span className="value">{getDisplayName(selectedMaterial.name)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">{t('dependentMaterialList.description')}:</span>
                    <span className="value">{getDisplayDescription(selectedMaterial.description)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">{t('dependentMaterialList.categories')}:</span>
                    <span className="value">{getCategoryNames(selectedMaterial.categories)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">{t('dependentMaterialList.unitOfMeasure')}:</span>
                    <span className="value">{selectedMaterial.unitOfMeasure || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">{t('dependentMaterialList.measureCapacity')}:</span>
                    <span className="value">{selectedMaterial.measureCapacity || 1}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">{t('dependentMaterialList.usagePerSqm')}:</span>
                    <span className="value">{selectedMaterial.usagePerSquareMeter || 0}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">{t('dependentMaterialList.price')}:</span>
                    <span className="value">{selectedMaterial.price || 0} {selectedMaterial.currency || 'AZN'}</span>
                  </div>
                  {selectedMaterial.servicePrice && (
                    <div className="detail-row">
                      <span className="label">{t('dependentMaterialList.servicePrice')}:</span>
                      <span className="value">{selectedMaterial.servicePrice} {selectedMaterial.currency || 'AZN'}</span>
                    </div>
                  )}
                  <div className="detail-row">
                    <span className="label">{t('dependentMaterialList.status')}:</span>
                    <span className={`value status ${(selectedMaterial.status === true || selectedMaterial.status === undefined) && (selectedMaterial.isActive === true || selectedMaterial.isActive === undefined) ? 'active' : 'inactive'}`}>
                      {(selectedMaterial.status === true || selectedMaterial.status === undefined) && (selectedMaterial.isActive === true || selectedMaterial.isActive === undefined)
                        ? t('dependentMaterialList.active') 
                        : t('dependentMaterialList.inactive')
                      }
                    </span>
                  </div>
                  {selectedMaterial.isDefault && (
                    <div className="detail-row">
                      <span className="label">{t('dependentMaterialList.isDefault')}:</span>
                      <span className="value default">{t('dependentMaterialList.yes')}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DependentMaterialList; 